import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useSession } from "../session/SessionContext";
import { ErrorState, LoadingState, PermissionDeniedState } from "../components/states";
import { GroupDetailSummary } from "../components/groups/GroupDetailSummary";
import { useSelectedPeriod } from "../hooks/useSelectedPeriod";
import {
  fetchMyGroup,
  fetchMyGroupDailySpend,
  fetchMyGroupPersonUsage,
  type MyGroupsDailySpendResponse,
  type MyGroupsResponse,
  type OrganisationPersonUsageResponse,
} from "../api/organisationReporting";
import { unchangedOr, usePolling } from "../hooks/usePolling";

const OthersConsumptionPermission = "consumption.read.others";

type PageState =
  | { kind: "loading" }
  | { kind: "loaded"; response: MyGroupsResponse }
  | { kind: "denied" }
  | { kind: "error"; message: string };

/**
 * One of the current person's groups on its own: its spend by tool, the groups directly beneath it, and the people
 * in it. Reached from the group's title on My Groups. The same permission as My Groups gates it, and the server
 * answers "not found" for a group the person cannot see.
 */
export function MyGroupDetailPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { snapshot } = useSession();
  const { groupId = "" } = useParams();
  const [periodStart] = useSelectedPeriod();
  const [state, setState] = useState<PageState>({ kind: "loading" });
  const [dailySpend, setDailySpend] = useState<MyGroupsDailySpendResponse | null>(null);
  const [personUsage, setPersonUsage] = useState<OrganisationPersonUsageResponse | null>(null);

  const hasPermission = snapshot.status === "signed-in" && snapshot.permissions.includes(OthersConsumptionPermission);

  const load = useCallback(
    // A quiet load refreshes the figures in place, without the loading layout, and keeps them if it fails.
    async (signal?: AbortSignal, quiet = false) => {
      if (!quiet) {
        setState({ kind: "loading" });
      }
      // Fetched together and committed together, so the charts are never drawn against the previous period's data.
      const [result, dailyResult, personResult] = await Promise.all([
        fetchMyGroup(groupId, periodStart, signal),
        fetchMyGroupDailySpend(groupId, periodStart, signal),
        fetchMyGroupPersonUsage(groupId, periodStart, signal),
      ]);
      if (result.kind === "aborted" || dailyResult.kind === "aborted" || personResult.kind === "aborted") {
        return;
      }

      if (quiet && result.kind !== "ok") {
        return;
      }

      switch (result.kind) {
        case "denied":
          setState({ kind: "denied" });
          return;
        case "error":
          setState({ kind: "error", message: result.message });
          return;
        case "ok": {
          const next: PageState = { kind: "loaded", response: result.response };
          setState((previous) => unchangedOr(previous, next));
        }
      }

      // The daily series and the per-person table are supplementary: left null on failure, not a second error state.
      setDailySpend((previous) => unchangedOr(previous, dailyResult.kind === "ok" ? dailyResult.response : null));
      setPersonUsage((previous) => unchangedOr(previous, personResult.kind === "ok" ? personResult.response : null));
    },
    [groupId, periodStart],
  );

  useEffect(() => {
    const controller = new AbortController();
    if (hasPermission) {
      void load(controller.signal);
    }
    return () => controller.abort();
  }, [hasPermission, load]);

  usePolling(() => load(undefined, true), hasPermission && state.kind === "loaded");

  if (!hasPermission) {
    return (
      <div>
        <PermissionDeniedState title={t("myGroups.denied.title")} description={t("myGroups.denied.description")} />
      </div>
    );
  }

  return (
    <div>
        {state.kind === "loading" && <LoadingState label={t("groupDetail.loading")} />}
        {state.kind === "denied" && <PermissionDeniedState title={t("myGroups.denied.title")} description={t("myGroups.denied.description")} />}
        {state.kind === "error" && (
          <ErrorState title={t("groupDetail.errorTitle")} description={state.message} onRetry={() => void load()} />
        )}
        {state.kind === "loaded" && (
          <GroupDetailSummary
            response={state.response}
            dailySpend={dailySpend}
            personUsage={personUsage}
            canSeeEveryone={snapshot.status === "signed-in" && snapshot.permissions.includes("scope.global")}
          />
        )}
    </div>
  );
}
